import React, { useState, useEffect } from "react";
import { Terminal, ArrowRight, Mic } from "lucide-react";
import OracleWorkspace from "./OracleWorkspace";
import { PRESENCE_STATES } from "./oracleVoice";
import { desk, fontMicro } from "../member-desk/tokens";

/**
 * OracleBar — the intelligence officer quietly standing watch at the top of
 * the Members headquarters. Submitting expands the immersive Oracle
 * workspace in place. Zero learning curve; feels native to the desk.
 */
export default function OracleBar() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState("");
  const [autoVoice, setAutoVoice] = useState(false);
  const [presence, setPresence] = useState(0);

  // Subtle, deterministic rotation of operational presence — Oracle exists
  // quietly rather than talking. Never random, never distracting.
  useEffect(() => {
    const t = setInterval(() => setPresence((i) => (i + 1) % PRESENCE_STATES.length), 9000);
    return () => clearInterval(t);
  }, []);

  const launch = (e) => {
    e.preventDefault();
    const t = query.trim();
    setPending(t);
    setAutoVoice(false);
    setOpen(true);
    setQuery("");
  };

  const launchVoice = () => {
    setPending("");
    setAutoVoice(true);
    setOpen(true);
  };

  return (
    <>
      <div
        className="relative border p-4 sm:p-5 mb-6 overflow-hidden"
        style={{
          borderColor: desk.edgeHi,
          backgroundColor: desk.surface,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.02)",
        }}
        data-testid="oracle-bar"
      >
        <div className={`${fontMicro} flex items-center gap-2 mb-3`} style={{ color: desk.inkMuted }}>
          <Terminal size={11} style={{ color: desk.redact }} />
          <span>ORACLE</span>
          <span data-testid="oracle-bar-status" className="transition-opacity duration-500">● {PRESENCE_STATES[presence]}</span>
        </div>
        <form onSubmit={launch} className="flex gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => { /* immediate immersive open on first intent */ }}
            placeholder="Ask Oracle…"
            data-testid="oracle-bar-input"
            className="flex-1 bg-[#0C0B0E] border px-4 py-3 text-[14px] font-mono placeholder:text-[#4A463F] focus:outline-none"
            style={{ borderColor: desk.edge, color: desk.ink }}
            onFocusCapture={(e) => (e.target.style.borderColor = desk.gold)}
            onBlur={(e) => (e.target.style.borderColor = desk.edge)}
          />
          <button
            type="button"
            onClick={launchVoice}
            data-testid="oracle-bar-mic"
            title="Open secure voice channel"
            className="inline-flex items-center justify-center px-4 border transition-colors"
            style={{ borderColor: desk.edge, color: desk.inkDim, backgroundColor: "#0C0B0E" }}
          >
            <Mic size={16} />
          </button>
          <button
            type="submit"
            data-testid="oracle-bar-launch"
            className="inline-flex items-center gap-2 px-4 sm:px-5 font-mono text-[11px] uppercase tracking-[0.25em] text-white"
            style={{ backgroundColor: desk.redact }}
          >
            Ask <ArrowRight size={12} />
          </button>
        </form>
      </div>

      <OracleWorkspace
        open={open}
        initialQuery={pending}
        autoVoice={autoVoice}
        onClose={() => { setOpen(false); setPending(""); setAutoVoice(false); }}
      />
    </>
  );
}
